import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Loader2, Save, Percent, Building2, Clock } from 'lucide-react';

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    sales_tax_rate: '',
    company_name: '',
    company_address: '',
    session_timeout_minutes: ''
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getSettings();
        setSettings(res.data);
        setForm({
          sales_tax_rate: res.data.sales_tax_rate?.toString() || '0',
          company_name: res.data.company_name || '',
          company_address: res.data.company_address || '',
          session_timeout_minutes: res.data.session_timeout_minutes?.toString() || '30'
        });
      } catch (err) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    const taxRate = parseFloat(form.sales_tax_rate);
    if (isNaN(taxRate) || taxRate < 0 || taxRate > 100) {
      toast.error('Tax rate must be between 0 and 100');
      return;
    }
    const timeout = parseInt(form.session_timeout_minutes);
    if (isNaN(timeout) || timeout < 5 || timeout > 480) {
      toast.error('Session timeout must be between 5 and 480 minutes');
      return;
    }

    setSaving(true);
    try {
      const res = await updateSettings({
        sales_tax_rate: taxRate,
        company_name: form.company_name.trim(),
        company_address: form.company_address.trim(),
        session_timeout_minutes: timeout
      });
      setSettings(res.data);
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="settings-page">
      <div>
        <h1 className="heading-2 text-[#1F2937]">Settings</h1>
        <p className="text-[#6B7280]">Configure application-wide settings</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Sales Tax */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
              <Percent size={20} className="text-[#0776BD]" /> Sales Tax
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-[#6B7280]">Set the sales tax rate applied to restock invoices, job costs, and reports.</p>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Tax Rate (%)</Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.sales_tax_rate}
                  onChange={(e) => setForm({ ...form, sales_tax_rate: e.target.value })}
                  className="h-12 rounded-lg border-gray-300 pr-10"
                  placeholder="0.00"
                  data-testid="tax-rate-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] font-medium">%</span>
              </div>
              <p className="text-xs text-[#9CA3AF]">Enter 0 to disable tax. Example: 8.375 for 8.375%</p>
            </div>
          </CardContent>
        </Card>

        {/* Company Info */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
              <Building2 size={20} className="text-[#0776BD]" /> Company Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-[#6B7280]">Displayed on exports and printed reports.</p>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Company Name</Label>
              <Input
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                className="h-12 rounded-lg border-gray-300"
                placeholder="e.g., Mr. Electric of Southwest Las Vegas"
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Company Address</Label>
              <Input
                value={form.company_address}
                onChange={(e) => setForm({ ...form, company_address: e.target.value })}
                className="h-12 rounded-lg border-gray-300"
                placeholder="e.g., 123 Main St, Las Vegas, NV 89101"
                data-testid="company-address-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Session Timeout */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
              <Clock size={20} className="text-[#0776BD]" /> Session Timeout
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-[#6B7280]">Automatically log out users after a period of inactivity.</p>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Timeout (minutes)</Label>
              <div className="relative">
                <Input
                  type="number"
                  min="5"
                  max="480"
                  value={form.session_timeout_minutes}
                  onChange={(e) => setForm({ ...form, session_timeout_minutes: e.target.value })}
                  className="h-12 rounded-lg border-gray-300 pr-14"
                  placeholder="30"
                  data-testid="session-timeout-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] text-sm">min</span>
              </div>
              <p className="text-xs text-[#9CA3AF]">Between 5 and 480 minutes. Takes effect on next login.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg gap-2 px-8"
          data-testid="save-settings-btn"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default Settings;
